// MSAL.js configuration for the itms-projects portal.
//
// Authority resolves to the WPI tenant. The clientId is the appId of the
// "WISE ITMS Projects {env}" Entra app registration. Single-tenant — the
// app registration's sign-in audience is AzureADMyOrg.
//
// VITE_* env vars are wired at build time. The pipeline's matrix build
// remaps VITE_*_TEST → plain VITE_* before `npm run build` so each
// environment gets its own portal bundle.

import { PublicClientApplication, type Configuration } from '@azure/msal-browser';

const TENANT_ID = (import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined) ?? '';
const CLIENT_ID = (import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined) ?? '';
const REDIRECT_URI = window.location.origin;

if (!TENANT_ID || !CLIENT_ID) {
  console.warn(
    '[msal] VITE_ENTRA_TENANT_ID or VITE_ENTRA_CLIENT_ID missing — auth will not work',
  );
}

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: REDIRECT_URI,
    postLogoutRedirectUri: REDIRECT_URI,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // localStorage, not sessionStorage (the platform rule, Chauncey White
    // 2026-10-23: every link lands signed in; 2026-11-02: "no matter waht app
    // i go to, i am forced to Authenticate. there is no token stored?"). A
    // link opens a NEW tab, and sessionStorage belongs to one tab — so every
    // link used to arrive with nobody signed in and ask again. With
    // localStorage the new tab finds the account and its refresh token and
    // never redirects at all. A shared PC is covered by the link's
    // ?login_hint beating whoever is cached (auth/ssoMemory.ts linkAccount);
    // Sign out clears this cache.
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    // The MSAL silent-token iframe (~6s default timeout) is too tight
    // when the operator has multiple WISE portal tabs open simultaneously
    // — each tab independently runs silent renewal against
    // login.microsoftonline.com and they serialize. Bumping to 10s matches
    // the wise-iam fix landed 2026-05-27. Note: msal-browser v4 uses
    // `iframeHashTimeout`; v5+ renamed it to `iframeBridgeTimeout`. This
    // portal is pinned to v4 (see package.json). See
    // https://aka.ms/msaljs/browser-errors#timed_out
    iframeHashTimeout: 10000,
    loadFrameTimeout: 10000,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Scopes requested for the itms-projects API audience. The user grants
// these on first sign-in (User-type scopes auto-consent; Admin-type
// scopes require admin consent in the Entra portal).
export const apiScopes = [`api://${CLIENT_ID}/qc.read`, `api://${CLIENT_ID}/qc.write`];

// Microsoft Graph scope for /me/photo/$value. Auto-consents on first sign-in.
export const graphScopes = ['User.Read'];
